// ─── AOPetrotecOTBadges — selos AO no card/detalhe da OT ──────────────────
//
// Componente sem estado: recebe a OT (já vinda do backend com finalCustomer
// e site em include) e devolve chips compactos. Se a OT não tem
// finalCustomerId, devolve `null` — em OTs PT é completamente invisível.
//
// Pode mostrar:
//   • Chip "Cliente · Posto" (vermelho discreto)
//   • Chip do tipo de intervenção (acidente/correctiva/preventiva)
//   • Badge SLA dinâmico: cumprido / em risco / falhado / em curso
//   • Chip "N equipamentos" (só se houver pelo menos 1)
//
// O cálculo do estado SLA é feito aqui no cliente, com base em:
//   · serviceDate ou createdAt como "início" do relógio
//   · slaHours (se preenchido na OT) ou um lookup do SLA contratual
//     (que NÃO faz fetch — usa-se apenas slaHours por simplicidade no card)
//   · deliveredAt como "fim" do relógio (se já entregue)
// Para o cálculo completo (que carregaria SLAContract), é mais útil no
// detalhe da OT do que no card — mantemos o card leve.

import React, { useContext } from 'react';
import { Box, Tooltip } from '@material-ui/core';
import { AuthContext } from '../../App';

// Alinhado com o BM oficial Petrotec (TIPO DE INTERVENÇÃO).
const INTERVENTION_COLORS = {
  accident:   { color: '#dc2626', bg: '#fee2e2', labelPt: 'P/Acidente', labelEn: 'Accident',   emoji: '🚨' },
  eventual:   { color: '#92400e', bg: '#fef3c7', labelPt: 'Eventual',   labelEn: 'Eventual',   emoji: '🔧' },
  preventive: { color: '#075985', bg: '#dbeafe', labelPt: 'Preventiva', labelEn: 'Preventive', emoji: '📋' },
  wear:       { color: '#6b21a8', bg: '#f3e8ff', labelPt: 'P/Desgaste', labelEn: 'Wear',       emoji: '⚙' },
  // Legacy — para OTs antigas
  corrective: { color: '#475569', bg: '#f1f5f9', labelPt: 'Correctiva', labelEn: 'Corrective', emoji: '🔧' },
};

// Estado SLA — calculado em runtime. Alinhado com a lógica do dashboard
// Custos & SLA: relógio começa na CHEGADA AO POSTO (arrivedAt) e pára no
// REGRESSO À BASE (returnedBaseAt). Antes contava de createdAt → delivery
// o que marcava como "falhado" OTs que nem sequer tinham sido despachadas.
function computeSlaStatus(order, nowMs) {
  const hours = order.slaHours;
  if (hours == null) return null;
  const r = order.assistanceReport;
  const startIso = r?.arrivedAt || null;
  // Sem chegada ao posto → relógio ainda não começou (Por iniciar).
  if (!startIso) return { state: 'pending_start' };

  const startMs = new Date(startIso).getTime();
  if (isNaN(startMs)) return null;
  const deadlineMs = startMs + Number(hours) * 3600 * 1000;

  const endIso = r?.returnedBaseAt || null;
  if (endIso) {
    const endMs = new Date(endIso).getTime();
    if (endMs <= deadlineMs) return { state: 'met', deadlineMs, endMs };
    return { state: 'failed', deadlineMs, endMs };
  }
  // No terreno (chegou mas ainda não regressou) — usar agora
  if (nowMs > deadlineMs) return { state: 'failed', deadlineMs };
  const remaining = deadlineMs - nowMs;
  const total = deadlineMs - startMs;
  if (remaining / total < 0.25) return { state: 'risk', deadlineMs, remaining };
  return { state: 'ok', deadlineMs, remaining };
}

function formatRemaining(ms) {
  if (ms < 0) return '0';
  const totalMin = Math.floor(ms / 60000);
  if (totalMin < 60) return `${totalMin}min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h < 24) return m === 0 ? `${h}h` : `${h}h${m}m`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh === 0 ? `${d}d` : `${d}d${rh}h`;
}

function AOPetrotecOTBadges({ order, lang = 'pt', size = 'small', showBmButton = true }) {
  const { token } = useContext(AuthContext);
  if (!order || !order.finalCustomerId) return null;

  const pt = lang === 'pt';
  const small = size === 'small';

  // Abre o PDF do BM em nova aba. Usa fetch com Authorization e cria
  // um Blob URL — não podemos usar <a href> directo porque precisamos
  // do header de auth.
  async function openBmPdf(e) {
    e.stopPropagation();
    try {
      const r = await fetch(`/planner-api/transport-orders/${order.id}/bm-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        alert(err.error || `Erro a gerar BM (HTTP ${r.status})`);
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Libertar após 30s para o browser conseguir renderizar
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      alert(err.message || 'Erro a abrir BM');
    }
  }
  const baseChip = {
    display: 'inline-flex', alignItems: 'center', gap: 3,
    marginTop: 2, marginRight: 4,
    padding: small ? '1px 6px' : '2px 8px',
    borderRadius: 4,
    fontSize: small ? '0.54rem' : '0.62rem',
    fontWeight: 800, letterSpacing: '0.03em',
    whiteSpace: 'nowrap', maxWidth: '100%',
  };

  const tMeta = order.interventionType ? INTERVENTION_COLORS[order.interventionType] : null;
  const sla = computeSlaStatus(order, Date.now());
  const equipCount = order._count?.equipmentInterventions ?? 0;

  const fcName = order.finalCustomer?.name;
  const siteName = order.site?.name;

  const slaConfigs = {
    met:           { bg: '#dcfce7', color: '#166534', label: pt ? 'SLA cumprido'  : 'SLA met',        emoji: '✓' },
    failed:        { bg: '#fee2e2', color: '#991b1b', label: pt ? 'SLA falhado'   : 'SLA missed',     emoji: '✕' },
    risk:          { bg: '#ffedd5', color: '#9a3412', label: pt ? 'SLA em risco'  : 'SLA at risk',    emoji: '⏳' },
    ok:            { bg: '#eff6ff', color: '#1d4ed8', label: pt ? 'SLA em curso'  : 'SLA running',   emoji: '⏱' },
    pending_start: { bg: '#fef3c7', color: '#92400e', label: pt ? 'SLA por iniciar' : 'SLA pending', emoji: '⏸' },
  };
  const slaCfg = sla ? slaConfigs[sla.state] : null;
  const slaLabel = slaCfg
    ? (sla.state === 'ok' || sla.state === 'risk'
        ? `${slaCfg.emoji} ${slaCfg.label} · ${formatRemaining(sla.remaining)}`
        : `${slaCfg.emoji} ${slaCfg.label}`)
    : null;

  return (
    <>
      {(fcName || siteName) && (
        <Tooltip title={
          pt
            ? `Cliente final · Posto: ${fcName || '?'}${siteName ? ` · ${siteName}` : ''}`
            : `Final customer · Site: ${fcName || '?'}${siteName ? ` · ${siteName}` : ''}`
        }>
          <Box style={{ ...baseChip, background: '#fef2f2', color: '#b91c1c' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {fcName}{siteName ? ` · ${siteName}` : ''}
            </span>
          </Box>
        </Tooltip>
      )}
      {tMeta && (
        <Tooltip title={pt ? 'Tipo de intervenção' : 'Intervention type'}>
          <Box style={{ ...baseChip, background: tMeta.bg, color: tMeta.color }}>
            <span>{tMeta.emoji}</span>
            <span>{pt ? tMeta.labelPt : tMeta.labelEn}</span>
          </Box>
        </Tooltip>
      )}
      {slaCfg && (
        <Tooltip title={
          sla.state === 'failed' && sla.deliveredMs
            ? (pt ? 'Entregue depois do prazo' : 'Delivered past deadline')
            : sla.state === 'met'
              ? (pt ? 'Entregue dentro do prazo' : 'Delivered within deadline')
              : (pt ? 'Tempo restante até ao prazo' : 'Time remaining until deadline')
        }>
          <Box style={{ ...baseChip, background: slaCfg.bg, color: slaCfg.color }}>
            <span>{slaLabel}</span>
          </Box>
        </Tooltip>
      )}
      {equipCount > 0 && (
        <Tooltip title={pt ? 'Equipamentos intervencionados' : 'Equipments intervened'}>
          <Box style={{ ...baseChip, background: '#f1f5f9', color: '#475569' }}>
            <span>⚙</span>
            <span>{equipCount} {pt ? (equipCount === 1 ? 'equip.' : 'equips.') : (equipCount === 1 ? 'equip.' : 'equips.')}</span>
          </Box>
        </Tooltip>
      )}
      {/* Botão "Imprimir BM" — gera PDF Petrotec oficial. Quando a OT
          está em fluxo (não-delivered), o chip aparece aqui no card para
          o operador poder imprimir mesmo antes da entrega. Para OTs já
          entregues, o OrdersView mostra o mesmo botão no sítio do
          "Ver relatório" (mais coerente com a UX dos outros tipos). */}
      {showBmButton && (
        <Tooltip title={pt ? 'Gerar Boletim de Manutenção em PDF' : 'Generate BM PDF'}>
          <Box onClick={openBmPdf}
            style={{
              ...baseChip,
              background: '#16a34a', color: '#fff',
              cursor: 'pointer',
            }}>
            <span>🖨</span>
            <span>{pt ? 'BM PDF' : 'BM PDF'}</span>
          </Box>
        </Tooltip>
      )}
    </>
  );
}

export default AOPetrotecOTBadges;
